import type { FormikProps } from 'formik'
import { uniq } from 'lodash'

import type { LeasingSettings } from '@/resources/leasing'
import type { EndOfLeaseReminder } from '@/resources/leasing/types'

import type { LeasingSettingsFormFields } from '.'

export type ReminderType = 'EMAIL' | 'SMS'

export type GroupedReminders = {
  id: number
  offsetDays: number | string | null
  types: {
    id: number | null
    type: ReminderType
    value: boolean
  }[]
}

export type UpdatedReminder = {
  id: number | null
  type: ReminderType
  offsetDays: number | null
}

const MAX_REMINDERS = 5
export const DEFAULT_EMPLOYEE_START_OFFSET = 60
export const DEFAULT_EMPLOYEE_SELECTION_OPEN_OFFSET = 50
export const DEFAULT_ASSET_RETURN_OFFSET = 20

const createEmptyReminder = (idx: number): GroupedReminders => ({
  id: idx,
  offsetDays: null,
  types: [
    { id: null, type: 'EMAIL', value: false }, // Set Email as unchecked by default
    { id: null, type: 'SMS', value: false }, // Set SMS as unchecked by default
  ],
})

export const mapInitialReminders = (data: Array<EndOfLeaseReminder>): Array<GroupedReminders> => {
  // Prepare base grouped list with empty reminders
  let grouped = Array.from({ length: MAX_REMINDERS }, (_, i) => createEmptyReminder(i + 1))

  if (data.length === 0) return mapInitialDefaultReminders(grouped)

  // Sort by offsetDays (descending, but nulls last)
  const sortedData = [...data].sort((a, b) => {
    if (a.offsetDays === null) return 1
    if (b.offsetDays === null) return -1
    return Number(b.offsetDays) - Number(a.offsetDays)
  })

  // Extract existing offsetDays and unique values (excluding nulls)
  const uniqOffsetDays = uniq(
    sortedData
      .map(reminder => reminder.offsetDays)
      .filter((offset): offset is number => offset !== null)
  )

  // Extract pool of IDs where offsetDays is null (to assign later)
  const nullIdPool = sortedData.filter(el => el.offsetDays === null).map(el => el.id)

  // Fill grouped array with matched reminders
  grouped = grouped.map((row, index) => {
    const offsetDays = uniqOffsetDays[index] ?? null

    const filledTypes = row.types.map(typeEntry => {
      // Find corresponding reminder in sorted data
      const matching =
        offsetDays !== null
          ? sortedData.find(r => r.offsetDays === offsetDays && r.type === typeEntry.type)
          : null

      if (matching) {
        return { id: matching.id, type: typeEntry.type, value: true }
      }

      // If still empty (id === null), assign from nullIdPool
      const fallbackId = nullIdPool.shift() ?? null
      return {
        id: fallbackId,
        type: typeEntry.type,
        value: false,
      }
    })

    return { ...row, offsetDays, types: filledTypes }
  })

  // Check if the first row has offsetDays as null, indicating no valid reminders were found
  if (grouped[0].offsetDays === null) {
    grouped = mapInitialDefaultReminders(grouped)
  }

  return grouped
}

export const mapInitialDefaultReminders = (
  data?: Array<GroupedReminders>
): Array<GroupedReminders> => {
  let grouped = data || Array.from({ length: MAX_REMINDERS }, (_, i) => createEmptyReminder(i + 1))

  grouped = grouped.map(row => {
    if (row.id === 1) {
      row.offsetDays = row.offsetDays ?? 40
      row.types = row.types.map(typeEntry => ({
        id: typeEntry.id,
        type: typeEntry.type,
        value: typeEntry.type === 'EMAIL' ? true : typeEntry.value,
      }))
    }
    if (row.id === 2) {
      row.offsetDays = row.offsetDays ?? 30
      row.types = row.types.map(typeEntry => ({
        id: typeEntry.id,
        type: typeEntry.type,
        value: typeEntry.type === 'EMAIL' ? true : typeEntry.value,
      }))
    }
    if (row.id === 3) {
      row.offsetDays = row.offsetDays ?? 25
      row.types = row.types.map(typeEntry => ({
        id: typeEntry.id,
        type: typeEntry.type,
        value: true,
      }))
    }
    return { ...row }
  })

  return grouped
}

export const mapRemindersToUpdate = (
  data: Array<GroupedReminders>,
  endOfLeaseReminders: Array<EndOfLeaseReminder>
): Array<UpdatedReminder> => {
  // Skip update if: id is null, checkbox is unchecked, and offsetDays is unset
  const updatedReminders = data.flatMap(group =>
    group.types
      .filter(
        t =>
          !(t.id === null && !t.value) && !(t.id === null && t.value && group.offsetDays === null)
      )
      .map(t => {
        // If offsetDays is set, id exists, and checkbox is unchecked - reset offsetDays to null
        if (Number(t.id) && !t.value && Number(group.offsetDays) > 0) {
          return {
            id: t.id ? Number(t.id) : null,
            type: t.type,
            offsetDays: null,
          }
        }
        return {
          id: t.id ? Number(t.id) : null,
          type: t.type,
          offsetDays: group.offsetDays === null ? null : Number(group.offsetDays),
        }
      })
  )
  const isEqual = (a: EndOfLeaseReminder, b: UpdatedReminder) =>
    Number(a.id) === b.id && a.offsetDays === b.offsetDays && a.type === b.type

  // Filter out unchanged reminders - keep only modified entries that differ from endOfLeaseReminders
  const filteredReminders = updatedReminders.filter(
    updated => !endOfLeaseReminders.some(original => isEqual(original, updated))
  )

  return filteredReminders
}

export const showSpecificFields = (isThreeStepIT: boolean, lockFields?: string[]) => {
  const lockOpenOffset = lockFields?.includes('endofleaseDefaultEmployeeSelectionOpenOffset')
  const lockAssetReturnOffset = lockFields?.includes('endofleaseDefaultAssetReturnOffset')
  return {
    showEmployeeSelectionOpen: !lockOpenOffset && !isThreeStepIT,
    showAssetReturn: !lockAssetReturnOffset && !isThreeStepIT,
  }
}

/**
 *
 * Reset "Employee" timeline fields to safe values whenever end-of-lease
 * automation is toggled OFF. Values come from server data when present,
 * falling back to hardcoded defaults otherwise. Runs unconditionally
 * (not error-driven) so behavior is deterministic across all data scenarios.
 */

export const normalizeEmployeeFieldsOnDisable = (
  isThreeStepIT: boolean,
  form: FormikProps<LeasingSettingsFormFields>,
  data?: LeasingSettings
) => {
  const endOfLease = data?.endOfLease

  // eslint-disable-next-line no-console
  console.log({ errors: form.errors }, '\nendOfLease', endOfLease)

  const { showEmployeeSelectionOpen, showAssetReturn } = showSpecificFields(
    isThreeStepIT,
    data?.endOfLease?.lockFieldsLeasingSettings
  )

  form.setFieldValue(
    'employeeStartOffset',
    endOfLease?.defaultEmployeeStartOffset ?? DEFAULT_EMPLOYEE_START_OFFSET
  )

  if (showEmployeeSelectionOpen) {
    form.setFieldValue(
      'employeeSelectionOpenOffset',
      endOfLease?.defaultEmployeeSelectionOpenOffset ?? DEFAULT_EMPLOYEE_SELECTION_OPEN_OFFSET
    )
  }

  if (showAssetReturn) {
    form.setFieldValue(
      'assetReturnOffset',
      endOfLease?.defaultAssetReturnOffset ?? DEFAULT_ASSET_RETURN_OFFSET
    )
  }

  const filledReminders = data?.endOfLease?.reminders
    ? mapInitialReminders(data?.endOfLease?.reminders)
    : mapInitialDefaultReminders()
  form.setFieldValue('reminders', filledReminders)

  // Clear touched state so no stale error UI flashes for these fields
  form.setFieldTouched('employeeStartOffset', false)
  form.setFieldTouched('employeeSelectionOpenOffset', false)
  form.setFieldTouched('assetReturnOffset', false)
  form.setFieldTouched('reminders', false)
}
